import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import type { WootricUser } from '@/types/wootric';

interface JwtPayload {
  sub?: string;
  email?: string;
  preferred_username?: string;
  name?: string;
  exp?: number;
  iat?: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

interface UseUserProfileResult {
  user: WootricUser | null;
  isLoading: boolean;
  error: Error | null;
}

export function useUserProfile(): UseUserProfileResult {
  const { getAccessToken, userProfile } = useAuth();
  const [user, setUser] = useState<WootricUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);

        if (userProfile?.email) {
          setUser({
            email: userProfile.email,
            userId: userProfile.id,
          });
          setIsLoading(false);
          return;
        }

        if (!getAccessToken) {
          setIsLoading(false);
          return;
        }

        const token = await getAccessToken();

        if (!token) {
          setIsLoading(false);
          return;
        }

        const jwtPayload = decodeJwt(token);
        const email = jwtPayload?.email || jwtPayload?.preferred_username || '';
        const userId = jwtPayload?.sub || '';

        if (!email) {
          setIsLoading(false);
          return;
        }

        setUser({ email, userId });
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to get user profile'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [getAccessToken, userProfile]);

  return { user, isLoading, error };
}
